<template>
  <div>
    <div v-if="canAddParticipants">
      <h2 class="text-lg font-semibold mb-4">Mettre à jour la liste des participants</h2>
      <div class="flex justify-evenly">
        <MultiSelect
          name="stores"
          label="Marchands"
          class="m-4"
          v-model="stores"
          :options="storeOptions"
          :enableSelectAll="true"
          :searchKeywordThreshold="0"
          @keyword="fetchStoreOptions"
        />
        <MultiSelect
          name="organizations"
          label="Ajouter à partir d'une organisation"
          class="m-4"
          v-model="organizations"
          :options="organizationOptions"
          :enableSelectAll="true"
          :searchKeywordThreshold="0"
          @keyword="fetchOrganizationOptions"
        />
      </div>
      <div class="flex justify-end">
        <GenericButton @click="updateParticipants">Mettre à jour</GenericButton>
      </div>
      <hr class="my-2" />
    </div>
    <h2 class="text-lg font-semibold mb-4">Liste des participants</h2>
    <Alert v-if="!participants.length" type="info">Aucun participant à ce challenge.</Alert>
    <ul class="max-h-[65vh] overflow-y-auto divide-y divide-gray-200 border border-gray-200 rounded-md">
      <li v-for="(participant, index) in participants" :key="index" class="p-1">
        {{ participant.salesChannel }}
      </li>
    </ul>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { Alert, MultiSelect, GenericButton, parseFrDate, toCamelCase, toSnakeCase } from "@estaly/ui";

export default {
  components: {
    Alert,
    GenericButton,
    MultiSelect,
  },
  emits: ["challengeUpdated"],
  props: {
    challenge: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      participantsModalOpen: false,
      stores: [],
      participants: [],
      organizations: [],
    };
  },
  computed: {
    ...mapState("organizations", { organizationsData: "index" }),
    ...mapState("stores", { storesData: "index" }),

    storeOptions() {
      return this.storesData.map((store) => ({ value: store.id, label: store.salesChannel }));
    },

    organizationOptions() {
      return this.organizationsData.map((org) => ({ value: org.id, label: org.name }));
    },

    canAddParticipants() {
      return parseFrDate(this.challenge.startDate) > new Date();
    },
  },
  mounted() {
    this.fetchChallenge();
    if (this.canAddParticipants) {
      this.fetchStores();
      this.fetchOrganizationOptions();
    }
  },
  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("challenges", ["fetch", "update", "addOrganizations"]),
    ...mapActions("organizations", { fetchOrganizations: "fetchIndex" }),
    ...mapActions("stores", { fetchStores: "fetchIndex" }),
    ...mapActions("backoffice", ["withLoader"]),

    async fetchChallenge() {
      await this.withLoader(async () => {
        const response = await this.fetch({
          challengeId: this.challenge.id,
          includes: ["store_challenges"],
        });
        if (!response?.success) return;

        this.participants = toCamelCase(response.data.challenge.store_challenges);
        this.stores = this.participants.map((participant) => participant.storeId);
      });
    },

    async updateParticipants() {
      const storeChallengesAttributes = [];
      this.stores.forEach((store) => {
        const storeChallenge = this.participants.find((participant) => participant.storeId === store);
        if (storeChallenge) {
          storeChallengesAttributes.push({
            id: storeChallenge.id,
            storeId: store,
          });
        } else {
          storeChallengesAttributes.push({ storeId: store });
        }
      });
      this.participants.forEach((participant) => {
        if (!this.stores.includes(participant.storeId)) {
          storeChallengesAttributes.push({ id: participant.id, _destroy: true });
        }
      });
      const data = toSnakeCase({ storeChallengesAttributes, organizations: this.organizations });
      const response = await this.update({ challengeId: this.challenge.id, payload: data });
      if (!response?.success) return;

      await this.addOrganizationsToChallenge();
      await this.fetchChallenge();
      await this.notify({
        category: "simple",
        type: "success",
        title: "Liste des participants mis à jour",
      });
      this.$emit("challengeUpdated");
    },

    async addOrganizationsToChallenge() {
      await this.addOrganizations({ challengeId: this.challenge.id, organizations: this.organizations });
      this.organizations = [];
    },

    async fetchStoreOptions(keywords = null, page = 1) {
      await this.withLoader(async () => {
        await this.fetchStores({ keywords, page, perPage: 2000 });
      });
    },

    async fetchOrganizationOptions(keywords = null, page = 1) {
      await this.withLoader(async () => {
        await this.fetchOrganizations({ keywords, page, perPage: 2000 });
      });
    },
  },
};
</script>
